import type {
  BrandOfferFormData,
  DiscountConfig,
  OfferType,
  TierRule,
} from '@/types/BrandOffer.types';
import {
  effectiveTierValueType,
  needsCouponDiscount,
  needsTierRules,
  tierUsesUnitValue,
} from './brandOfferTilePreview.rules';
import { OFFER_PREVIEW_BASE_PRODUCT } from './OfferProductPreview.constants';
import type {
  ComputedOfferPrices,
  OfferPreviewInput,
} from './OfferProductPreview.types';

export { buildOfferBannerData } from './brandOfferTilePreview.utils';
export {
  needsCouponDiscount,
  needsOfferCopyLine,
  needsTextDiscount,
  needsTierRules,
  tierUsesUnitValue,
  effectiveTierValueType,
} from './brandOfferTilePreview.rules';

export function tierTemplateExampleValue(
  rules: TierRule[] | undefined,
  offerType: OfferType
): string {
  const active = pickTierRuleForTemplateExample(rules);
  if (!active) {
    return tierUsesUnitValue(offerType) ? '₹100' : '10%';
  }
  return renderTierValueWithSymbol(
    Number(active.value) > 0 ? active : { ...active, value: 100 },
    offerType
  );
}

export function tierBandMatchesCart(
  rule: TierRule,
  cartSubtotal: number
): boolean {
  const min = Number(rule.min ?? 0);
  if (!Number.isFinite(min) || cartSubtotal < min) return false;
  if (rule.max === null || rule.max === undefined) return true;
  const max = Number(rule.max);
  return Number.isFinite(max) && cartSubtotal <= max;
}

export function pickActiveTierRuleForCart(
  rules: TierRule[] | undefined,
  cartSubtotal: number
): TierRule | null {
  if (!rules?.length) return null;
  let firstInBand: TierRule | null = null;
  for (const r of rules) {
    if (!tierBandMatchesCart(r, cartSubtotal)) continue;
    if (!firstInBand) firstInBand = r;
    if (Number(r.value ?? 0) > 0) return r;
  }
  return firstInBand;
}

function pickTierRuleForTemplateExample(
  rules: TierRule[] | undefined
): TierRule | null {
  if (!rules?.length) return null;
  return rules.find((x) => Number(x.value) > 0) ?? rules[0] ?? null;
}

function sellingFromTierRule(
  mrp: number,
  rule: TierRule | null,
  offerType: OfferType
): number {
  if (!rule) return mrp;
  const val = Number(rule.value ?? 0);
  if (val <= 0) return mrp;
  const unitFlat = effectiveTierValueType(rule, offerType) === 'FLAT';
  return unitFlat ? Math.max(0, mrp - val) : Math.round(mrp * (1 - val / 100));
}

function renderTierValueWithSymbol(
  rule: TierRule,
  offerType: OfferType
): string {
  const kind = effectiveTierValueType(rule, offerType);
  const val = Number(rule.value ?? 0);
  return kind === 'PERCENT' ? `${val}%` : `₹${val}`;
}

export function computeOfferPrices(
  offerType: OfferType,
  discountConfig: DiscountConfig | null
): ComputedOfferPrices {
  const mrp: number = OFFER_PREVIEW_BASE_PRODUCT.price;
  let selling: number = mrp;
  let tierValueRendered: string | undefined;

  if (needsCouponDiscount(offerType) && discountConfig) {
    const val = Number(discountConfig.value ?? 0);
    if (discountConfig.type === 'FLAT' && val > 0) {
      selling = Math.max(0, mrp - val);
    } else if (discountConfig.type === 'PERCENT' && val > 0) {
      selling = Math.round(mrp * (1 - val / 100));
    }
  } else if (needsTierRules(offerType) && discountConfig?.rules?.length) {
    const active = pickActiveTierRuleForCart(discountConfig.rules, mrp);
    selling = sellingFromTierRule(mrp, active, offerType);
    if (active && Number(active.value) > 0) {
      tierValueRendered = renderTierValueWithSymbol(active, offerType);
    }
  }

  const showStrike = selling < mrp;
  let percentOff: number | null = null;
  if (showStrike && mrp > 0) {
    percentOff = Math.round(((mrp - selling) / mrp) * 100);
  }
  const flatSaving = showStrike ? mrp - selling : null;
  return {
    mrp,
    selling,
    showStrike,
    percentOff,
    flatSaving,
    tierValueRendered,
  };
}

export function formDataToPreviewInput(
  data: BrandOfferFormData,
  brandName: string
): OfferPreviewInput {
  return {
    offerType: data.offerType,
    brandName,
    description: OFFER_PREVIEW_BASE_PRODUCT.name,
    saleName: data.name,
    isActive: data.isActive,
    isSale: data.isSale,
    showTimer: data.showTimer,
    endsAt: data.endsAt,
    discountConfig: data.discountConfig,
    imageUrl: OFFER_PREVIEW_BASE_PRODUCT.imageUrl,
  };
}
